import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import { adminJson } from "../utils/adminSession";
import { formatAdminDateTime, isKlToday } from "../utils/formatAdminDate";
import AdminConfirmDialog from "../components/AdminConfirmDialog";

const ENQUIRY_TYPES = [
  "General Enquiry",
  "Request a Quote",
  "Product Availability",
  "Delivery Enquiry",
  "Supplier Partnership",
  "Other",
];
const PAGE_SIZE = 10;
const inputClass =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark outline-none focus:border-at-gold";

function idOf(item) {
  return String(item?._id || item?.id || "");
}

function formatReceived(value) {
  return formatAdminDateTime(value);
}

function typeClass(enquiry) {
  if (enquiry === "Request a Quote") return "bg-amber-50 text-amber-800";
  if (enquiry === "Product Availability") return "bg-violet-50 text-violet-800";
  if (enquiry === "Delivery Enquiry") return "bg-indigo-50 text-indigo-700";
  if (enquiry === "Supplier Partnership") return "bg-sky-50 text-sky-800";
  return "bg-neutral-100 text-neutral-600";
}

function csvCell(value) {
  const text = String(value ?? "");
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function whatsappHref(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : "";
}

function replySubject(item, siteName) {
  const subject = String(item?.subject || "").trim();
  const site = String(siteName || "").trim();
  const head = `RE:${subject}`;
  return site ? `${head} | ${site}` : head;
}

function AdminFormPage() {
  const navigate = useNavigate();
  // List is always newest-first; no sort control.
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [lastSelectedId, setLastSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : []))
      .then((list) => {
        if (!cancelled) setContact(Array.isArray(list) && list[0] ? list[0] : null);
      })
      .catch(() => {
        if (!cancelled) setContact(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    adminJson("/forms")
      .then((list) => {
        if (!cancelled) {
          setForms(Array.isArray(list) ? list : []);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        setError(err.message || "Unable to load form submissions");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const stats = useMemo(() => {
    const unread = forms.filter((item) => !item.read).length;
    return {
      all: forms.length,
      unread,
      read: forms.length - unread,
        today: forms.filter((item) => isKlToday(item.createdAt)).length,
    };
  }, [forms]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
    const next = forms.filter((item) => {
      const hay = [item.name, item.companyName, item.email, item.subject, item.message, item.enquiry]
        .map((value) => String(value || "").toLowerCase())
        .join(" ");
      if (q && !hay.includes(q)) return false;
      if (typeFilter !== "all" && item.enquiry !== typeFilter) return false;
      if (statusFilter === "unread" && item.read) return false;
      if (statusFilter === "read" && !item.read) return false;
      const created = new Date(item.createdAt);
      if (from && !(created >= from)) return false;
      if (to && !(created <= to)) return false;
      return true;
    });
    next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return next;
  }, [forms, search, typeFilter, statusFilter, dateFrom, dateTo]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const from = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(currentPage * PAGE_SIZE, filtered.length);
  const pageIds = paged.map(idOf);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const selectedForm = forms.find((item) => idOf(item) === lastSelectedId) || null;

  function closeDetails() {
    if (!selectedForm) {
      setLastSelectedId("");
      return;
    }
    const id = idOf(selectedForm);
    setLastSelectedId("");
    setSelectedIds((prev) => prev.filter((item) => item !== id));
  }

  useEffect(() => {
    if (!selectedForm) return undefined;

    function syncBodyScroll() {
      const mobile = window.matchMedia("(max-width: 1279px)").matches;
      document.body.style.overflow = mobile ? "hidden" : "";
    }

    function onKeyDown(event) {
      if (event.key !== "Escape") return;
      if (deleteTarget) return;
      closeDetails();
    }

    syncBodyScroll();
    window.addEventListener("resize", syncBodyScroll);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", syncBodyScroll);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [selectedForm, deleteTarget]);

  function clearFilters() {
    setSearch("");
    setTypeFilter("all");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  }

  function setStatusTab(next) {
    setStatusFilter(next);
    setPage(1);
  }

  function selectForm(id, checked) {
    setSelectedIds((prev) => {
      const next = checked
        ? prev.includes(id) ? prev : [...prev, id]
        : prev.filter((item) => item !== id);
      if (checked) setLastSelectedId(id);
      else setLastSelectedId((current) => (current === id ? next[next.length - 1] || "" : current));
      return next;
    });
  }

  function togglePage(checked) {
    if (checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
      setLastSelectedId(pageIds[pageIds.length - 1] || "");
      return;
    }
    setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    setLastSelectedId((prev) => (pageIds.includes(prev) ? "" : prev));
  }

  async function markRead(ids, read) {
    const targets = forms.filter((item) => ids.includes(idOf(item)) && Boolean(item.read) !== read);
    if (!targets.length) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const updated = await Promise.all(
        targets.map((item) => adminJson(`/forms/${idOf(item)}`, { method: "PUT", body: { read } }))
      );
      setForms((prev) =>
        prev.map((item) => {
          const next = updated.find((row) => idOf(row) === idOf(item));
          return next || item;
        })
      );
      setNotice(read ? "Selected submissions marked as read." : "Submission marked as unread.");
    } catch (err) {
      if (err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err.message || "Unable to update submissions");
    } finally {
      setSaving(false);
    }
  }

  function requestDelete(item) {
    setDeleteTarget(item);
  }

  async function deleteForm(item) {
    const id = idOf(item);
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await adminJson(`/forms/${id}`, { method: "DELETE" });
      setForms((prev) => prev.filter((row) => idOf(row) !== id));
      setSelectedIds((prev) => prev.filter((rowId) => rowId !== id));
      setLastSelectedId((prev) => (prev === id ? "" : prev));
      setNotice("Submission deleted.");
      setDeleteTarget(null);
    } catch (err) {
      if (err.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err.message || "Unable to delete submission");
    } finally {
      setSaving(false);
    }
  }

  function exportCsv() {
    const rows = [
      ["Name", "Company", "Email", "Phone", "Type", "Subject", "Location", "Message", "Source", "Received Date", "Status", "Reference"],
      ...filtered.map((item) => [
        item.name,
        item.companyName,
        item.email,
        item.phone,
        item.enquiry,
        item.subject,
        item.location,
        item.message,
        item.sourcePage,
        formatReceived(item.createdAt),
        item.read ? "Read" : "Unread",
        item.referenceNo,
      ]),
    ];
    const csv = rows.map((row) => row.map(csvCell).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "form-submissions.csv";
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-400">Enquiry Management</p>
          <h1 className="mt-1 text-2xl font-semibold text-at-dark">Form Submissions</h1>
          <p className="mt-1 text-sm text-neutral-500">Review and manage contact enquiries and quotation requests.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportCsv}
            className="rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-at-dark hover:border-at-gold"
          >
            Export CSV
          </button>
          <button
            type="button"
            disabled={saving || selectedIds.length === 0}
            onClick={() => markRead(selectedIds, true)}
            className="rounded-md bg-at-gold px-3 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-40"
          >
            Mark Selected as Read
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="All Submissions" value={stats.all} />
        <StatCard label="Today" value={stats.today} />
        <StatCard label="Unread" value={stats.unread} />
        <StatCard label="Read" value={stats.read} />
      </div>

      <div className="mt-5 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
        <input
          type="search"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search name, company, email or subject"
          className={`${inputClass} xl:col-span-2`}
        />
        <select value={typeFilter} onChange={(event) => { setTypeFilter(event.target.value); setPage(1); }} className={inputClass}>
          <option value="all">All Enquiry Types</option>
          {ENQUIRY_TYPES.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
        <div className="flex gap-2">
          <input type="date" value={dateFrom} onChange={(event) => { setDateFrom(event.target.value); setPage(1); }} className={inputClass} aria-label="From date" />
          <input type="date" value={dateTo} onChange={(event) => { setDateTo(event.target.value); setPage(1); }} className={inputClass} aria-label="To date" />
        </div>
      </div>
      <button type="button" onClick={clearFilters} className="mt-2 text-sm text-neutral-500 hover:text-at-dark">
        Clear Filters
      </button>

      {error ? <p className="mt-4 text-sm text-red-600">{error}</p> : null}
      {notice ? <p className="mt-4 text-sm text-emerald-700">{notice}</p> : null}

      <div className="mt-5 grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <section className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <div className="flex flex-wrap gap-1 border-b border-neutral-100 px-3 py-2 text-sm">
            <TabButton active={statusFilter === "all"} onClick={() => setStatusTab("all")}>All {stats.all}</TabButton>
            <TabButton active={statusFilter === "unread"} onClick={() => setStatusTab("unread")}>Unread {stats.unread}</TabButton>
            <TabButton active={statusFilter === "read"} onClick={() => setStatusTab("read")}>Read {stats.read}</TabButton>
          </div>

          {loading ? (
            <p className="px-4 py-10 text-sm text-neutral-500">Loading submissions…</p>
          ) : filtered.length === 0 ? (
            <p className="px-4 py-10 text-sm text-neutral-500">No form submissions match the current filters.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wide text-neutral-500">
                  <tr>
                    <th className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={(event) => togglePage(event.target.checked)}
                        aria-label="Select all on this page"
                      />
                    </th>
                    <th className="px-4 py-3">Contact</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Subject</th>
                    <th className="px-4 py-3">Received Date</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((item) => {
                    const id = idOf(item);
                    const checked = selectedIds.includes(id);
                    const active = lastSelectedId === id;
                    return (
                      <tr
                        key={id}
                        onClick={() => selectForm(id, true)}
                        className={`cursor-pointer border-t border-neutral-100 ${
                          !item.read ? "bg-amber-50/70" : "bg-white"
                        } ${active ? "ring-1 ring-inset ring-at-gold/40" : ""}`}
                      >
                        <td className="px-4 py-3" onClick={(event) => event.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => selectForm(id, event.target.checked)}
                            aria-label={`Select ${item.name}`}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <span
                              className={`mt-0.5 w-5 shrink-0 text-center text-2xl font-black leading-none text-red-600 ${
                                item.read ? "" : "admin-unread-bang"
                              }`}
                              aria-hidden={item.read}
                              aria-label={item.read ? undefined : "Unread"}
                            >
                              {item.read ? "" : "!"}
                            </span>
                            <div className="min-w-0">
                              <p className="font-medium text-at-dark">{item.name || "—"}</p>
                              <p className="text-xs text-neutral-500">{item.companyName || "—"}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${typeClass(item.enquiry)}`}>
                            {item.enquiry || "—"}
                          </span>
                        </td>
                        <td className="max-w-[220px] truncate px-4 py-3 text-neutral-600">{item.subject || "—"}</td>
                        <td className="whitespace-nowrap px-4 py-3 text-neutral-500">{formatReceived(item.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length > 0 ? (
            <div className="flex flex-col items-center justify-between gap-3 border-t border-neutral-100 px-4 py-3 text-sm text-neutral-500 sm:flex-row">
              <p>
                Showing {from}–{to} of {filtered.length}
              </p>
              <div className="flex items-center gap-1">
                <button type="button" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 disabled:opacity-40" aria-label="Previous page">
                  ‹
                </button>
                {Array.from({ length: pageCount }, (_, i) => i + 1).slice(0, 7).map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className={`min-w-[2rem] rounded-md px-2 py-1.5 ${n === currentPage ? "bg-at-gold text-at-dark" : "border border-neutral-200 bg-white"}`}
                  >
                    {n}
                  </button>
                ))}
                <button type="button" disabled={currentPage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} className="rounded-md border border-neutral-200 bg-white px-2 py-1.5 disabled:opacity-40" aria-label="Next page">
                  ›
                </button>
              </div>
            </div>
          ) : null}
        </section>

        <aside className="sticky top-8 hidden rounded-xl border border-neutral-200 bg-white xl:block xl:min-h-[36rem]">
          {selectedForm ? (
            <DetailPanel
              item={selectedForm}
              siteName={String(contact?.siteName || "").trim()}
              saving={saving}
              onClose={closeDetails}
              onToggleRead={() => markRead([idOf(selectedForm)], !selectedForm.read)}
              onReply={() => markRead([idOf(selectedForm)], true)}
              onDelete={() => requestDelete(selectedForm)}
            />
          ) : (
            <div className="flex h-full min-h-[24rem] flex-col items-center justify-center px-6 text-center">
              <p className="text-sm font-medium text-at-dark">No submission selected</p>
              <p className="mt-1 text-sm text-neutral-500">Select a form to view contact details, enquiry text and reply actions.</p>
            </div>
          )}
        </aside>
      </div>
      {selectedForm ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4 xl:hidden">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close submission details"
            onClick={closeDetails}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Submission details"
            className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl"
          >
            <DetailPanel
              item={selectedForm}
              siteName={String(contact?.siteName || "").trim()}
              saving={saving}
              onClose={closeDetails}
              onToggleRead={() => markRead([idOf(selectedForm)], !selectedForm.read)}
              onReply={() => markRead([idOf(selectedForm)], true)}
              onDelete={() => requestDelete(selectedForm)}
            />
          </div>
        </div>
      ) : null}
      {deleteTarget ? (
        <AdminConfirmDialog
          title="Delete submission?"
          message={`Delete “${deleteTarget.subject || deleteTarget.name || "this submission"}”? You can restore it from Recent Activities for 30 days.`}
          busy={saving}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => deleteForm(deleteTarget)}
        />
      ) : null}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="min-w-0 rounded-xl border border-neutral-200 bg-white px-3 py-4 sm:px-4">
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-400">{label}</p>
      <p className="mt-2 text-3xl font-semibold text-at-dark">{value}</p>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 ${active ? "bg-neutral-100 font-medium text-at-dark" : "text-neutral-500 hover:text-at-dark"}`}
    >
      {children}
    </button>
  );
}

function DetailPanel({ item, siteName, saving, onClose, onToggleRead, onReply, onDelete }) {
  const wa = whatsappHref(item.phone);
  const mail = item.email
    ? `mailto:${item.email}?subject=${encodeURIComponent(replySubject(item, siteName))}`
    : "";

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex items-start justify-between gap-3 border-b border-neutral-100 px-5 py-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-400">Submission Details</p>
          <h2 className="mt-1 truncate text-lg font-semibold text-at-dark">{item.name || "—"}</h2>
          <p className="truncate text-sm text-neutral-500">{item.companyName || "—"}</p>
        </div>
        <button type="button" onClick={onClose} className="text-xl leading-none text-neutral-400 hover:text-at-dark" aria-label="Close details">
          ×
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 px-5 py-3">
        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${item.read ? "bg-neutral-100 text-neutral-600" : "bg-amber-100 text-amber-800"}`}>
          {item.read ? "READ" : "UNREAD"}
        </span>
        <button
          type="button"
          disabled={saving}
          onClick={onToggleRead}
          className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-at-dark hover:border-at-gold disabled:opacity-40"
        >
          {item.read ? "Mark as unread" : "Mark as Read"}
        </button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto px-5 pb-5">
        <section>
          <h3 className="text-sm font-semibold text-at-dark">Contact Information</h3>
          <p className="mt-2 text-sm text-neutral-600">{item.name || "—"}</p>
          <p className="text-sm text-neutral-600">{item.email || "—"}</p>
          <p className="text-sm text-neutral-600">{item.phone || "—"}</p>
          <p className="text-sm text-neutral-600">{item.location || "—"}</p>
        </section>
        <section>
          <h3 className="text-sm font-semibold text-at-dark">Enquiry</h3>
          <p className="mt-2 text-sm text-neutral-600">Type: {item.enquiry || "—"}</p>
          <p className="text-sm text-neutral-600">Subject: {item.subject || "—"}</p>
          <p className="mt-2 whitespace-pre-wrap text-sm text-neutral-600">{item.message || "—"}</p>
        </section>
        <section>
          <h3 className="text-sm font-semibold text-at-dark">Metadata</h3>
          <p className="mt-2 text-sm text-neutral-600">Received Date: {formatReceived(item.createdAt)}</p>
          <p className="text-sm text-neutral-600">Source: {item.sourcePage || "—"}</p>
          <p className="text-sm text-neutral-600">Reference: {item.referenceNo || "—"}</p>
        </section>
      </div>

      <div className="space-y-2 border-t border-neutral-100 px-5 py-4">
        {mail ? (
          <a
            href={mail}
            onClick={onReply}
            className="flex items-center justify-center rounded-md bg-at-gold px-3 py-2.5 text-sm font-semibold text-at-dark hover:bg-at-gold-hover"
          >
            Reply by Email
          </a>
        ) : (
          <p className="rounded-md bg-neutral-100 px-3 py-2.5 text-center text-sm text-neutral-400">No email to reply</p>
        )}
        {wa ? (
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onReply}
            className="flex items-center justify-center rounded-md border border-neutral-200 px-3 py-2.5 text-sm font-semibold text-at-dark hover:border-at-gold"
          >
            Open WhatsApp
          </a>
        ) : (
          <p className="rounded-md border border-neutral-100 px-3 py-2.5 text-center text-sm text-neutral-400">No WhatsApp number</p>
        )}
        <button
          type="button"
          disabled={saving}
          onClick={onDelete}
          className="flex w-full items-center justify-center rounded-md border border-red-200 px-3 py-2.5 text-sm font-semibold text-red-600 hover:border-red-400 hover:bg-red-50 disabled:opacity-40"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export default AdminFormPage;
